import { DataSourceViewsProvider } from "@app/components/agent_builder/DataSourceViewsContext";
import { PreviewPanelProvider } from "@app/components/agent_builder/PreviewPanelContext";
import { SpacesProvider } from "@app/components/shared/SpacesContext";
import { MCPServerViewsProvider } from "@app/components/shared/tools_picker/MCPServerViewsContext";
import type { FetchAgentTemplateResponse } from "@app/lib/resources/template_resource";
import type { TemplateActionPreset } from "@app/types/assistant/templates";
import type { UserType, WorkspaceType } from "@app/types/user";
import type { ReactNode } from "react";
import { createContext, useContext, useMemo, useState } from "react";

type AgentBuilderContextType = {
  owner: WorkspaceType;
  user: UserType;
  isAdmin: boolean;
  assistantTemplate: FetchAgentTemplateResponse | null;
  presetActionToAdd: TemplateActionPreset | null;
  setPresetActionToAdd: (preset: TemplateActionPreset | null) => void;
};

export const AgentBuilderContext = createContext<
  AgentBuilderContextType | undefined
>(undefined);

interface AgentBuilderProviderProps {
  owner: WorkspaceType;
  user: UserType;
  isAdmin: boolean;
  assistantTemplate: FetchAgentTemplateResponse | null;
  children: ReactNode;
}

export function AgentBuilderProvider({
  owner,
  user,
  isAdmin,
  assistantTemplate,
  children,
}: AgentBuilderProviderProps) {
  const [presetActionToAdd, setPresetActionToAdd] =
    useState<TemplateActionPreset | null>(null);

  const value = useMemo(
    () => ({
      owner,
      user,
      isAdmin,
      assistantTemplate,
      presetActionToAdd,
      setPresetActionToAdd,
    }),
    [owner, user, isAdmin, assistantTemplate, presetActionToAdd]
  );

  return (
    <AgentBuilderContext.Provider value={value}>
      <PreviewPanelProvider>
        <SpacesProvider owner={owner}>
          <MCPServerViewsProvider owner={owner}>
            <DataSourceViewsProvider owner={owner}>
              {children}
            </DataSourceViewsProvider>
          </MCPServerViewsProvider>
        </SpacesProvider>
      </PreviewPanelProvider>
    </AgentBuilderContext.Provider>
  );
}

export function useAgentBuilderContext() {
  const context = useContext(AgentBuilderContext);
  if (!context) {
    throw new Error(
      "useAgentBuilderContext must be used within an AgentBuilderProvider"
    );
  }
  return context;
}
